import React from 'react';
import { Document, Page, Text, Font, View, StyleSheet } from '@react-pdf/renderer';
import CoverPage from './sections/CoverPage';
import DisclaimerPage from './sections/DisclaimerPage';
import TableOfContentsPage from './sections/TableOfContentsPage';
import DocumentReferencePage from './sections/DocumentReferencePage';
import ExecutiveSummaryPage from './sections/ExecutiveSummaryPage';
import KeyFindingsPage from './sections/KeyFindingsPage';
import ScopePage from './sections/ScopePage';
import ProjectObjectivesPage from './sections/ProjectObjectivesPage';
import SummaryOfFindingsPage from './sections/SummaryOfFindingsPage';
import VulnerabilityRatingDefinitionsPage from './sections/VulnerabilityRatingDefinitionsPage';
import KeyFindingsListPage from './sections/KeyFindingsListPage';
import DetailedFindingsBySeverityPage from './sections/DetailedFindingsBySeverityPage';
import AppendixOwaspRiskRatingPages from './sections/AppendixOwaspRiskRatingPages';
import MethodologyPage from './sections/MethodologyPage';
import NetworkMethodologyPage from './sections/NetworkMethodologyPage';
import MobileMethodologyPage from './sections/MobileMethodologyPage';
import SourceCodeMethodologyPage from './sections/SourceCodeMethodologyPage';
import AboutUsPage from './sections/AboutUsPage';
import { ReportData } from '../types/report.types';
import { SectionPageProvider, useSectionPages } from './SectionPageContext';
import { filterReportDataByRunId } from '../utils/findingsUtils';
import { processDisclaimerContent, registerReportFonts, sortFindings } from '../utils/reportSharedUtils';

// Register fonts once globally
registerReportFonts();

interface ReportTemplatePDFProps {
  reportData: ReportData;
  pieChartImage?: string;
  barChartImage?: string;
  processedFindings?: Record<string, any[]> | null;
  processing?: boolean;
}

const styles = StyleSheet.create({
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 10,
    color: 'grey',
  },
});

const ReportTemplatePDFContent: React.FC<ReportTemplatePDFProps> = ({
  reportData: rawReportData,
  pieChartImage,
  barChartImage,
  processedFindings,
  processing,
}) => {
  const reportData = React.useMemo(() => {
    const data = filterReportDataByRunId(rawReportData) || rawReportData;
    if (data.detailed_findings) {
      return {
        ...data,
        detailed_findings: sortFindings(data.detailed_findings)
      };
    }
    return data;
  }, [rawReportData]);

  const disclaimerParagraphs = processDisclaimerContent((reportData.disclaimer ?? '') || '');
  const { updateSectionPage } = useSectionPages();

  if (processing) {
    return (
      <Document>
        <Page>
          <Text>Loading...</Text>
        </Page>
      </Document>
    );
  }

  // Helper to render a page with footer
  const renderPageWithFooter = (children: React.ReactNode, key?: string | number) => (
    <React.Fragment key={key}>
      {React.cloneElement(children as React.ReactElement, {},
        <>
          {(children as React.ReactElement).props.children}
          <View style={styles.footer} fixed>
            <Text render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`} />
          </View>
        </>
      )}
    </React.Fragment>
  );

  return (
    <Document>
      <CoverPage reportData={reportData} />
      <DisclaimerPage
        reportData={reportData}
        disclaimerParagraphs={disclaimerParagraphs}
        processDisclaimerContent={processDisclaimerContent}
        sectionId="Disclaimer"
      />
      {renderPageWithFooter(<DocumentReferencePage reportData={reportData} sectionId="DocumentReference" />, 'doc-ref')}
      <TableOfContentsPage reportData={reportData} />
      {renderPageWithFooter(
        <ExecutiveSummaryPage
          reportData={reportData}
          pieChartImage={pieChartImage}
          barChartImage={barChartImage}
          sectionId="ExecutiveSummary"
        />, 'exec-summary'
      )}
      {renderPageWithFooter(<KeyFindingsPage reportData={reportData} sectionId="KeyFindings" />, 'key-findings')}
      {renderPageWithFooter(<ScopePage reportData={reportData} sectionId="Scope" />, 'scope')}
      {renderPageWithFooter(<ProjectObjectivesPage reportData={reportData} sectionId="ProjectObjectives" />, 'objectives')}
      {renderPageWithFooter(
        <SummaryOfFindingsPage
          reportData={reportData}
          pieChartImage={pieChartImage}
          barChartImage={barChartImage}
          sectionId="SummaryOfFindings"
        />, 'summary-findings'
      )}
      {renderPageWithFooter(<VulnerabilityRatingDefinitionsPage reportData={reportData} sectionId="VulnerabilityRatingDefinitions" />, 'rating-defs')}
      {renderPageWithFooter(<KeyFindingsListPage reportData={reportData} sectionId="KeyFindingsList" />, 'key-findings-list')}
      
      {/* Detailed Findings Section */}
      {processedFindings && Object.keys(processedFindings).length > 0 && (
        ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'].map(severity => (
          <DetailedFindingsBySeverityPage
            key={severity}
            severity={severity.charAt(0) + severity.slice(1).toLowerCase()}
            findings={processedFindings[severity] || []}
            documentNumber={reportData.document_number}
            reportData={reportData}
            registerSectionPage={updateSectionPage}
            sectionId={`DetailedFindings_${severity.charAt(0) + severity.slice(1).toLowerCase()}`}
          />
        ))
      )}
      {AppendixOwaspRiskRatingPages({ documentNumber: reportData.document_number || 'Document Number', registerSectionPage: updateSectionPage, sectionId: 'AppendixOwaspRiskRating_Page1' })
        .filter(React.isValidElement)
        .map((page, idx) => React.cloneElement(page, { key: `appendix-${idx}` }))}
      {(reportData.methodology?.web) && (
        <MethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="Methodology" />
      )}
      {(reportData.methodology?.network) && (
        <NetworkMethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="NetworkMethodology" />
      )}
      {(reportData.methodology?.mobile) && (
        <MobileMethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="MobileMethodology" />
      )}
      {(reportData.methodology?.sourceCode) && (
        <SourceCodeMethodologyPage documentNumber={reportData.document_number} registerSectionPage={updateSectionPage} sectionId="SourceCodeMethodology" />
      )}
      <AboutUsPage />
    </Document>
  );
};

const ReportTemplatePDF: React.FC<ReportTemplatePDFProps> = (props) => (
  <SectionPageProvider>
    <ReportTemplatePDFContent {...props} />
  </SectionPageProvider>
);

export default ReportTemplatePDF; 